/**
 * The "is this interesting?" inbox — one row per organisation Kate pulled out
 * of a lead source. Presentational: the page owns the query and the verdict
 * mutation; a row only reports which verdict was clicked (with an optional
 * one-line note Kate reads to calibrate).
 */
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";

import type { LeadCandidate, Verdict } from "../../api/leadSources";
import { formatRelative } from "../../lib/formatRelative";
import { canUndo, fitTone, handoffPill } from "../../lib/leadSources";
import { LinkAnchor } from "../LinkAnchor";
import { Markdown } from "../Markdown";
import { Pill } from "../Pill";

interface Props {
  leads: LeadCandidate[];
  busyId: string | null;
  onVerdict: (lead: LeadCandidate, verdict: Verdict, note?: string) => Promise<void>;
  onOpenSource: (slug: string) => void;
}

function CrmHint({ lead }: { lead: LeadCandidate }) {
  const hint = lead.crm_hint;
  if (!hint) return null;
  const company = hint.companies[0];
  return (
    <span className="pill pill--muted" title="Already in the CRM – check before handing it on">
      in CRM{company ? `: ${company.name ?? company.id}` : ""}
      {hint.contact_count ? ` · ${hint.contact_count} contact${hint.contact_count > 1 ? "s" : ""}` : ""}
    </span>
  );
}

function LeadRow({
  lead,
  busy,
  onVerdict,
  onOpenSource,
}: {
  lead: LeadCandidate;
  busy: boolean;
  onVerdict: Props["onVerdict"];
  onOpenSource: Props["onOpenSource"];
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const stage = handoffPill(lead.handoff_stage);
  const decide = (verdict: Verdict) => onVerdict(lead, verdict, note).then(() => setNote(""));

  return (
    <Fragment>
      <tr className="cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <td>
          <div className="font-heading font-semibold text-ink">{lead.name}</div>
          {lead.website ? (
            <a
              href={lead.website}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-ink-low"
              onClick={(e) => e.stopPropagation()}
            >
              {lead.host ?? lead.website} ↗
            </a>
          ) : null}
        </td>
        <td className="text-sm text-ink-medium max-w-[420px]">{lead.why}</td>
        <td>
          <Pill value={lead.fit} tone={fitTone(lead.fit)} />
        </td>
        <td className="text-sm text-ink-medium whitespace-nowrap">{lead.segment}</td>
        <td className="whitespace-nowrap">
          {lead.source_slugs.map((slug) => (
            <button
              key={slug}
              type="button"
              className="pill pill--muted mr-1"
              onClick={(e) => {
                e.stopPropagation();
                onOpenSource(slug);
              }}
            >
              {slug}
            </button>
          ))}
        </td>
        <td className="text-sm text-ink-low tnum whitespace-nowrap">
          {formatRelative(lead.created_at)}
        </td>
        <td className="text-right whitespace-nowrap space-x-1.5" onClick={(e) => e.stopPropagation()}>
          {lead.status === "proposed" ? (
            <>
              <button
                type="button"
                disabled={busy}
                className="btn btn-main btn-sm"
                onClick={() => decide("interesting")}
              >
                Interesting → Seth
              </button>
              <button
                type="button"
                disabled={busy}
                className="btn btn-ghost btn-sm"
                onClick={() => decide("not_interesting")}
              >
                Not interesting
              </button>
            </>
          ) : (
            <>
              {lead.status === "interesting" && stage ? (
                <Pill value={stage.label} tone={stage.tone} />
              ) : (
                <Pill value={lead.status} tone={lead.status === "withdrawn" ? "muted" : undefined} />
              )}
              {canUndo(lead) ? (
                <button
                  type="button"
                  disabled={busy}
                  className="btn btn-ghost btn-sm"
                  title="Back to undecided – only while Seth hasn't picked it up"
                  onClick={() => decide("proposed")}
                >
                  Undo
                </button>
              ) : null}
            </>
          )}
        </td>
      </tr>
      {open ? (
        <tr style={{ background: "var(--c-bg-tint)" }}>
          <td colSpan={7}>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 py-2">
              <div className="space-y-3">
                <Markdown text={lead.rationale_md} />
                {lead.evidence.length ? (
                  <ul className="text-sm space-y-1">
                    {lead.evidence.map((e) => (
                      <li key={e.url}>
                        <LinkAnchor href={e.url}>{e.title || e.url}</LinkAnchor>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap gap-1.5">
                  <CrmHint lead={lead} />
                  {lead.pipelines.map((p) => (
                    <span key={p} className="pill pill--muted" title="Seth enrolls contacts here">
                      {p}
                    </span>
                  ))}
                </div>
                {lead.verdict_note ? (
                  <div className="text-ink-medium">
                    <span className="label">Your note</span>
                    {lead.verdict_note}
                  </div>
                ) : null}
                {lead.withdraw_reason ? (
                  <div className="text-ink-medium">
                    <span className="label">Withdrawn by Kate</span>
                    {lead.withdraw_reason}
                  </div>
                ) : null}
                {lead.handoff_task ? (
                  <div className="text-ink-medium">
                    <span className="label">Hand-off</span>
                    <Link className="link" to={`/tasks?task=${lead.handoff_task.id}`}>
                      {lead.handoff_task.title ?? "task"}
                    </Link>
                    {lead.handoff_task.agent_error ? (
                      <div className="text-xs text-danger">
                        {lead.handoff_task.agent_error}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {lead.status === "proposed" ? (
                  <div>
                    <label className="label" htmlFor={`note-${lead.id}`}>
                      Note for Kate (optional)
                    </label>
                    <input
                      id={`note-${lead.id}`}
                      className="input w-full"
                      maxLength={500}
                      value={note}
                      placeholder="e.g. too early, no regulatory plan yet"
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

export function LeadsTable({ leads, busyId, onVerdict, onOpenSource }: Props) {
  return (
    <div className="panel panel--flush overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th>Organisation</th>
            <th>Why</th>
            <th>Fit</th>
            <th>Segment</th>
            <th>Sources</th>
            <th>Proposed</th>
            <th className="text-right">Decision</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <LeadRow
              key={lead.id}
              lead={lead}
              busy={busyId === lead.id}
              onVerdict={onVerdict}
              onOpenSource={onOpenSource}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
